import React, { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const TEAL = '#0d9488'
const PURPLE = '#7c3aed'
const PURPLE_MID   = '#c4b5fd'
const PURPLE_LIGHT = '#f5f3ff'
const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"

function ClinicSummary({ onSelectPatient, isDemo = false, clinicId, featureFlags = {} }) {
  const canPlateau  = featureFlags.plateau  !== false
  const canOutcomes = featureFlags.outcomes !== false
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!clinicId && !isDemo) return
    setLoading(true)
    setSummary(null)
    const summaryUrl = clinicId
      ? API + '/summary?clinic_id=' + clinicId + '&demo=' + isDemo
      : API + '/summary?demo=true'
    axios.get(summaryUrl)
      .then(res => { setSummary(res.data); setLoading(false) })
      .catch(() => setLoading(false))
  }, [isDemo, clinicId])

  if (loading) return <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '15px', fontFamily: FONT }}>Loading...</div>
  if (!summary) return <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '15px', fontFamily: FONT }}>Could not load overview.</div>

  return (
    <div style={{ fontFamily: FONT }}>
      <style>{`
        /* Large monitor */
        @media (min-width: 1440px) {
          .cs-table-header, .cs-table-row { grid-template-columns: 2fr 1.5fr 160px 140px 100px !important; padding-left: 28px !important; padding-right: 28px !important; }
          .cs-stat-grid { grid-template-columns: repeat(5, 1fr) !important; }
          .cs-stat-card { padding: 24px 28px !important; }
          .cs-stat-value { font-size: 36px !important; }
        }
        /* Mobile */
        @media (max-width: 640px) {
          .cs-table-header { display: none !important; }
          .cs-table-row {
            display: flex !important;
            flex-direction: column !important;
            padding: 14px 16px !important;
            gap: 6px !important;
          }
          .cs-cell-recovery { max-width: 200px !important; }
          .cs-cell-btn { display: flex !important; justify-content: flex-start !important; margin-top: 4px !important; }
        }
        @media (max-width: 960px) and (min-width: 641px) {
          .cs-table-header, .cs-table-row { grid-template-columns: 2fr 1.5fr 120px 100px !important; }
          .cs-cell-btn { display: none !important; }
          .cs-table-row { cursor: pointer; }
        }
      `}</style>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>Overview</h1>
      </div>

      <div className="cs-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1px', background: 'transparent', borderRadius: '10px', overflow: 'visible', marginBottom: '28px' }}>
        {[
          { label: 'Total patients', value: summary.total_patients, show: true },
          { label: 'Progressing',    value: summary.progressing,              accent: '#16a34a', show: canOutcomes },
          { label: 'Plateauing',     value: summary.plateauing,               accent: summary.plateauing > 0 ? '#dc2626' : undefined, show: canPlateau },
          { label: 'Avg recovery',   value: summary.avg_recovery_pct + '%',   accent: TEAL,      show: canOutcomes },
          { label: 'No sessions',    value: summary.no_data,                  accent: PURPLE,    show: canOutcomes },
        ].filter(c => c.show).map(card => (
          <div className="cs-stat-card" key={card.label} style={{ background: 'rgba(255,255,255,0.78)', padding: '20px 24px' }}>
            <div className="cs-stat-value" style={{ fontSize: '30px', fontWeight: '700', color: card.accent || '#0f172a', letterSpacing: '-0.03em', lineHeight: 1 }}>{card.value}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '6px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{card.label}</div>
          </div>
        ))}
        {!canOutcomes && (
          <div className="cs-stat-card" key="locked-outcomes" style={{ background: 'rgba(248,250,252,0.78)', padding: '20px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 4 }}>
            <div style={{ fontSize: '20px' }}></div>
            <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '600', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Outcomes Analytics</div>
            <div style={{ fontSize: '10px', color: '#cbd5e1' }}>Upgrade to unlock</div>
          </div>
        )}
      </div>

      {canPlateau && summary.patients.filter(p => p.plateau_flag).length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '16px 20px', marginBottom: '24px' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#dc2626', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Needs attention</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {summary.patients.filter(p => p.plateau_flag).map(p => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.78)', borderRadius: '8px', padding: '10px 14px', border: '1px solid #fecaca' }}>
                <div>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{p.name}</span>
                  <span style={{ fontSize: '13px', color: '#94a3b8', marginLeft: '8px' }}>{p.injury_type}</span>
                </div>
                <button onClick={() => onSelectPatient(p)} style={{ padding: '5px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>View</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cs-patient-table" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(13,148,136,0.15)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>All patients</div>
          <div style={{ fontSize: '13px', color: '#94a3b8' }}>{summary.patients.length} total</div>
        </div>

        <div className="cs-table-header" style={{ display: 'grid', gridTemplateColumns: canOutcomes ? '2fr 1.5fr 120px 110px 80px' : '2fr 1.5fr 110px 80px', padding: '8px 20px', background: 'rgba(248,250,252,0.84)', borderBottom: '1px solid rgba(124,58,237,0.15)' }}>
          {(canOutcomes ? ['Patient', 'Injury', 'Recovery', 'Status', ''] : ['Patient', 'Injury', 'Status', '']).map((h, i) => (
            <div key={i} style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
          ))}
        </div>

        {summary.patients.map((p, i) => (
          <div key={p.id}
            className="cs-table-row"
            onClick={() => onSelectPatient(p)}
            style={{ display: 'grid', gridTemplateColumns: canOutcomes ? '2fr 1.5fr 120px 110px 80px' : '2fr 1.5fr 110px 80px', padding: '12px 20px', borderTop: i === 0 ? 'none' : '1px solid rgba(13,148,136,0.12)', alignItems: 'center', cursor: 'pointer', background: 'rgba(255,255,255,0.78)', transition: 'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,250,252,0.90)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.78)'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: PURPLE_LIGHT, border: '1.5px solid ' + PURPLE_MID, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: PURPLE, flexShrink: 0 }}>
                {p.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a' }}>{p.name}</span>
            </div>
            <div style={{ fontSize: '13px', color: '#64748b' }}>{p.injury_type || '—'}</div>
            {canOutcomes && (
              <div className="cs-cell-recovery">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ flex: 1, height: '4px', background: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: p.recovery_pct + '%', background: p.recovery_pct < 33 ? '#f87171' : p.recovery_pct < 66 ? '#fb923c' : '#34d399', borderRadius: '99px' }} />
                  </div>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', width: '30px', flexShrink: 0 }}>{p.recovery_pct}%</span>
                </div>
              </div>
            )}
            <div className="cs-cell-status">
              {canPlateau ? (
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '99px', background: p.sessions_completed === 0 ? '#f1f5f9' : p.plateau_flag ? '#fef2f2' : '#f0fdf4', color: p.sessions_completed === 0 ? '#94a3b8' : p.plateau_flag ? '#dc2626' : '#16a34a' }}>
                  {p.sessions_completed === 0 ? 'No sessions' : p.plateau_flag ? 'Plateau' : 'Progressing'}
                </span>
              ) : (
                <span style={{ fontSize: '11px', fontWeight: '600', padding: '3px 8px', borderRadius: '99px', background: p.sessions_completed === 0 ? '#f1f5f9' : '#f0fdf4', color: p.sessions_completed === 0 ? '#94a3b8' : '#16a34a' }}>
                  {p.sessions_completed === 0 ? 'No sessions' : 'Active'}
                </span>
              )}
            </div>
            <div className="cs-cell-btn">
              <button onClick={e => { e.stopPropagation(); onSelectPatient(p) }} style={{ padding: '5px 12px', background: TEAL, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: FONT }}>View</button>
            </div>
          </div>
        ))}

        {summary.patients.length === 0 && (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '15px' }}>
            No patients yet. Click "Add Patient" to get started.
          </div>
        )}
      </div>
    </div>
  )
}

export default ClinicSummary